// frontend/src/components/Mobile/MobilePlanoTeares.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { fastReactAPI } from '../../../api/client';
import { FiCpu, FiFilter, FiX, FiCheck } from 'react-icons/fi';
import MobileLayout from '../MobileLayout';
import '../Mobile.css';

const PAGE_SIZE = 50;

const STATUS_FILTERS = [
  { value: '',          label: 'Todos' },
  { value: 'PENDING',   label: 'SETUP' },
  { value: 'RESTOCK',   label: 'REABASTECER' },
  { value: 'READY',     label: 'PRONTO' },
  { value: 'COMPLETED', label: 'CONCLUIDO' },
];

// Always trust po_setup.status first — isCompleted is only a fallback
// for legacy rows where status was never set.
const statusBadge = (status, isCompleted) => {
  if (status === 'COMPLETED')
    return <span className="mb-badge mb-badge--completed">CONCLUIDO</span>;
  if (status === 'READY')
    return <span className="mb-badge mb-badge--completed">PRONTO</span>;
  if (status === 'IN_PROGRESS')
    return <span className="mb-badge mb-badge--inprog">Em Preparação</span>;
  if (status === 'SETUP')
    return <span className="mb-badge mb-badge--setup">SETUP</span>;
  if (status === 'RESTOCK')
    return <span className="mb-badge mb-badge--restock">REABASTECER</span>;
  if (isCompleted)
    return <span className="mb-badge mb-badge--completed">CONCLUIDO</span>;
  return null;
};

const MobilePlanoTeares = () => {
  const navigate = useNavigate();

  const [rows,         setRows]         = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [total,        setTotal]        = useState(0);
  const [page,         setPage]         = useState(1);
  const [linhaOptions, setLinhaOptions] = useState([]);
  const [statusCounts, setStatusCounts] = useState({ setup: 0, restock: 0 });

  // 8. Default to PENDING (SETUP)
  const [filters,  setFilters]  = useState({ status: 'PENDING', linhas: [],
                                              date_from: '', date_to: '' });
  const [search,   setSearch]   = useState('');
  const [showMachPicker, setShowMachPicker] = useState(false);

  const searchDebounce = useRef(null);

  const load = useCallback(async (f, p, s) => {
    setLoading(true);
    try {
      const params = { page: p, page_size: PAGE_SIZE, print_status: 'true' };
      if (f.status)          params.status    = f.status;
      if (f.linhas?.length)  params.machine   = f.linhas.join(',');
      if (s)                 params.po_code   = s;
      if (f.date_from)       params.date_from = f.date_from;
      if (f.date_to)         params.date_to   = f.date_to;
      const r = await fastReactAPI.getPlanoTeares(params);
      if (r.data.success) {
        console.log('[MobilePlanoTeares] rows:', r.data.data?.length, 'first po_setup_id:', r.data.data?.[0]?.po_setup_id);
        setRows(r.data.data);
        setTotal(r.data.total);
        if (r.data.machines?.length) setLinhaOptions(r.data.machines);
        if (r.data.status_counts) setStatusCounts(r.data.status_counts);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(filters, page, search); }, [filters, page]);

  const handleSearch = (v) => {
    setSearch(v);
    clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => load(filters, 1, v), 400);
  };

  const toggleLinha = (l) => {
    setFilters(prev => {
      const has = prev.linhas.includes(l);
      return { ...prev, linhas: has ? prev.linhas.filter(x => x !== l) : [...prev.linhas, l] };
    });
    setPage(1);
  };

  const grouped = rows.reduce((acc, row, idx) => {
    const prev = rows[idx - 1];
    acc.push({ row, newMachine: !prev || prev.machine !== row.machine });
    return acc;
  }, []);

  const activeLinhaCount = filters.linhas.length;

  return (
    <MobileLayout title="Plano Teares">
      {/* Search + machine filter button */}
      <div style={{ display:'flex', gap:8, marginBottom:8 }}>
        <div className="mb-search" style={{ flex:1, marginBottom:0 }}>
          <input type="text" placeholder="Pesquisar ordem…"
            value={search} onChange={e => handleSearch(e.target.value)} />
          {search && <span className="mb-search-clear" onClick={() => handleSearch('')}>×</span>}
        </div>
        <button type="button"
          className={`mb-machine-picker-btn ${activeLinhaCount ? 'mb-machine-picker-btn--active' : ''}`}
          onClick={() => setShowMachPicker(true)}>
          <FiFilter size={15} />
          {activeLinhaCount > 0 && <span className="mb-machine-picker-count">{activeLinhaCount}</span>}
        </button>
      </div>

      {/* Setup date filter */}
      <div style={{ display:'flex', gap:6, marginBottom:8, alignItems:'center' }}>
        <div style={{ fontSize:11, color:'var(--gray-500)', whiteSpace:'nowrap',
                      fontWeight:600, textTransform:'uppercase', letterSpacing:'0.04em' }}>
          Data Setup
        </div>
        <input type="date" value={filters.date_from}
          onChange={e => { setFilters(p => ({ ...p, date_from: e.target.value })); setPage(1); }}
          style={{ flex:1, height:34, border:'1px solid var(--gray-300)', borderRadius:8,
                   padding:'0 8px', fontSize:13, outline:'none', minWidth:0 }} />
        <span style={{ fontSize:11, color:'var(--gray-400)' }}>–</span>
        <input type="date" value={filters.date_to}
          onChange={e => { setFilters(p => ({ ...p, date_to: e.target.value })); setPage(1); }}
          style={{ flex:1, height:34, border:'1px solid var(--gray-300)', borderRadius:8,
                   padding:'0 8px', fontSize:13, outline:'none', minWidth:0 }} />
        {(filters.date_from || filters.date_to) && (
          <button type="button" onClick={() => { setFilters(p => ({ ...p, date_from:'', date_to:'' })); setPage(1); }}
            style={{ background:'none', border:'none', color:'var(--gray-400)',
                     cursor:'pointer', fontSize:16, padding:'0 4px', lineHeight:1 }}>×</button>
        )}
      </div>

      {/* Status filter pills */}
      <div className="mb-filters">
        {STATUS_FILTERS.map(f => {
          const badgeCount = f.value === 'PENDING' ? statusCounts.setup
                           : f.value === 'RESTOCK' ? statusCounts.restock
                           : null;
          return (
          <button key={f.value} type="button"
            className={`mb-filter-pill ${filters.status === f.value ? 'mb-filter-pill--active' : ''}`}
            onClick={() => { setFilters(prev => ({ ...prev, status: f.value })); setPage(1); }}>
            {f.label}
              {badgeCount != null && <span className="mb-filter-pill-badge">{badgeCount}</span>}
          </button>
          );
        })}
        {/* Show active linha pills */}
        {filters.linhas.map(l => (
          <button key={l} type="button" className="mb-filter-pill mb-filter-pill--active"
            onClick={() => toggleLinha(l)}>
            {l} ×
          </button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div className="mb-state"><div className="mb-spinner" /></div>
      ) : rows.length === 0 ? (
        <div className="mb-state">
          <FiCpu size={32} style={{ color: 'var(--gray-300)' }} />
          <span>Sem ordens</span>
        </div>
      ) : (
        <div className="mb-card-list">
          {grouped.map(({ row, newMachine }) => {
            const pct = row.total_items > 0
              ? Math.round((row.prepared_items / row.total_items) * 100) : 0;
            const total_qty = (parseFloat(row.strip_quantity) || 0)
                            + (parseFloat(row.strip_qty_made) || 0);

            return (
              <React.Fragment key={row.po_setup_id}>
                {newMachine && row.machine && (
                  <div>
                    <div className="mb-machine-divider" />
                    <div className="mb-machine-label">
                      <FiCpu size={11} /> Máquina {row.machine}
                    </div>
                  </div>
                )}
                <div className="mb-card"
                  onClick={() => {
                    if (!row.po_setup_id) {
                      console.error('[MobilePlanoTeares] po_setup_id missing on row:', row);
                      return;
                    }
                    navigate(`/mobile/plano-teares/${row.po_setup_id}`);
                  }}>
                  <div className="mb-card-header">
                    <div className="mb-linha-dot">{row.machine || '?'}</div>
                    <div className="mb-card-info">
                      <div className="mb-card-po">
                        {row.machine_cluster && (
                          <span className="mb-cluster-badge">{row.machine_cluster}</span>
                        )}
                        {row.po_code_line_full}
                      </div>
                      <div className="mb-card-product">
                        {row.product_name || '—'}
                        {row.customer_name ? ` · ${row.customer_name}` : ''}
                      </div>
                      {row.container_material && (
                        <div className="mb-card-product">
                          🛒 {row.container_material}
                        </div>
                      )}
                    </div>
                    <div className="mb-card-right">
                      <div className="mb-prog-wrap">
                        <div className="mb-prog-bar">
                          <div className="mb-prog-fill" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="mb-prog-label">{row.prepared_items}/{row.total_items}</span>
                      </div>
                      {total_qty > 0 && (
                        <span style={{ fontSize: 11, color: 'var(--gray-400)' }}>{total_qty.toLocaleString('pt-PT')}</span>
                      )}
                    </div>
                  </div>
                  <div className="mb-card-footer">
                    {statusBadge(row.status, row.is_completed)}
                    {row.strip_start && (
                      <span className="mb-badge mb-badge--null">
                        {new Date(row.strip_start).toLocaleDateString('pt-PT')}
                      </span>
                    )}
                  </div>
                </div>
              </React.Fragment>
            );
          })}

          {rows.length < total && (
            <button type="button" className="mb-btn-primary" style={{ marginTop: 8 }}
              onClick={() => setPage(p => p + 1)} disabled={loading}>
              Ver mais ({total - rows.length} restantes)
            </button>
          )}
        </div>
      )}

      {/* 7. Machine multi-select picker */}
      {showMachPicker && (
        <div className="mb-picker-overlay" onClick={() => setShowMachPicker(false)}>
          <div className="mb-picker-sheet" onClick={e => e.stopPropagation()}>
            <div className="mb-picker-header">
              <span className="mb-picker-title">Selecionar Máquinas</span>
              <button type="button" className="mb-icon-btn" onClick={() => setShowMachPicker(false)}>
                <FiX size={18} />
              </button>
            </div>

            {filters.linhas.length > 0 && (
              <button type="button" className="mb-picker-clear"
                onClick={() => { setFilters(prev => ({ ...prev, linhas: [] })); setPage(1); }}>
                Limpar seleção ({filters.linhas.length})
              </button>
            )}

            <div className="mb-picker-list">
              {linhaOptions.map(l => {
                const sel = filters.linhas.includes(l);
                return (
                  <div key={l}
                    className={`mb-picker-row ${sel ? 'mb-picker-row--selected' : ''}`}
                    onClick={() => toggleLinha(l)}>
                    <span className="mb-picker-linha">{l}</span>
                    {sel && <FiCheck size={16} style={{ color: 'var(--primary)' }} />}
                  </div>
                );
              })}
            </div>

            <button type="button" className="mb-btn-primary mb-picker-done"
              style={{ height: 52, fontSize: 15 }}
              onClick={() => setShowMachPicker(false)}>
              Aplicar{filters.linhas.length > 0 ? ` (${filters.linhas.length} selecionadas)` : ''}
            </button>
          </div>
        </div>
      )}
    </MobileLayout>
  );
};

export default MobilePlanoTeares;